import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { userSettingsLabels } from '../UserSettingsForm/UserSettings';
import styles from './UserSettingsAndAccessForm.module.css';
import SettingsGroup from './SettingsGroup';
import AccessCheckbox from './AccessCheckbox';
import type { PermissionMap, UserSettings, UserOrganizationSettings, UserData } from 'admin/types';
import { BlockedByPersonaText } from 'admin/utils/shouldRender';
import { PermissionKey } from 'js-sdk/src/types/Persona';
import { mobxInjectSelect } from 'common/utils';
import { DotsIndicator } from 'common/components';
import { useAppSelector } from 'redux-stores';
import TCClient from 'client';

type MobxProps = {
  featureFlags: Record<string, unknown>;
  getURLParams: () => Record<string, string>;
  loadPermissions: (
    selectedOrganizationId: string
  ) => Promise<{ name: PermissionKey; description: string }[]>;
};

type UserSettingsAndAccessFormProps = {
  disableAll?: boolean;
  isPersonaForm?: boolean;
  userPermissions: PermissionMap | undefined;
  userSettings: UserSettings;
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>;
  setUserPermissions: React.Dispatch<React.SetStateAction<PermissionMap | undefined>>;
  userData?: UserData;
};

function PersonasSettingsAndAccessForm({
  disableAll = false,
  featureFlags,
  isPersonaForm = false,
  getURLParams,
  loadPermissions,
  userPermissions = {},
  userSettings,
  setUserSettings,
  setUserPermissions,
}: UserSettingsAndAccessFormProps & MobxProps) {
  const [userOrgSettings, setUserOrgSettings] = useState<UserOrganizationSettings>();
  const [orgPermissions, setOrgPermissions] = useState<
    Map<PermissionKey, { name: string; description: string }>
  >(new Map());
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const organizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const urlParams = getURLParams();

  useEffect(() => {
    TCClient.adminUsers
      .fetchOrganizationSettings({ organizationId })
      .then((org_settings) => setUserOrgSettings(org_settings));
    loadPermissions(organizationId).then((permissions) =>
      setOrgPermissions(new Map(permissions.map((p) => [p.name, p])))
    );
  }, [loadPermissions, organizationId]);

  useEffect(() => {
    const reduceRes = Array.from(orgPermissions.keys()).reduce(
      (pMap, permission) => ({
        ...pMap,
        [permission]: userPermissions?.[permission] || false,
      }),
      {}
    );
    setPermissions(reduceRes);
  }, [isPersonaForm, orgPermissions, userPermissions]);

  if (!userSettings || !userOrgSettings) {
    return (
      <div className={classNames(styles.loadingHolder)}>
        <DotsIndicator color="#0e0d0d" size={12} marginTop={30} marginRight={5} />
      </div>
    );
  }

  const { settings, patientEngagementSettings } = userSettingsLabels(
    userSettings,
    userOrgSettings,
    setUserSettings,
    featureFlags,
    urlParams,
    disableAll,
    'v2',
    () => false
  );

  // TODO: This is temporary to work around server bug (USR-96), needs to be removed
  //       once the issue is resolved.
  const filteredSettings = settings.filter(
    (setting) => !['Message Lifespan', 'PIN Lock (mobile)'].includes(setting.label)
  );
  return (
    <div className={styles.userSettingsAndAccessForm}>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <SettingsGroup
          isPersonaForm={isPersonaForm}
          title="General Settings"
          settings={filteredSettings}
          userOrgSettings={(userOrgSettings || {}) as UserOrganizationSettings}
        />
        <SettingsGroup
          isPersonaForm={isPersonaForm}
          title="Patient Engagement Settings"
          settings={patientEngagementSettings}
          userOrgSettings={(userOrgSettings || {}) as UserOrganizationSettings}
        />
      </div>
      <hr className={classNames(styles.personaHr)} />
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupTitle}>Access</div>
        <div className={styles.settingsGroupGrid}>
          {Object.keys(permissions).map((permission) => {
            return (
              <AccessCheckbox
                key={permission}
                isLockedByPersona={false}
                permission={permission}
                blockedByPersonaText={BlockedByPersonaText}
                orgPermissions={orgPermissions}
                disableAll={disableAll}
                checked={permissions[permission as keyof PermissionMap]}
                onClick={(checked: boolean) =>
                  setUserPermissions((prev) => ({ ...prev, [permission]: checked }))
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default mobxInjectSelect<UserSettingsAndAccessFormProps, MobxProps>({
  featureStore: ['featureFlags', 'getURLParams'],
  personaStore: ['loadPermissions'],
})(PersonasSettingsAndAccessForm);
